'use client';
import { getFailedAutogiroTransactionAttemptsFilters } from '@/app/dashboard/invoicing/failed-autogiro-transaction-attempts/constants';
import { useFailedInvoices } from '@/app/dashboard/invoicing/failed-deliveries/utils';
import { getPendingAutogiroTransactionAttemptsFilters } from '@/app/dashboard/invoicing/pending-autogiro-transaction-attempts/constants';
import { useIndeterminateVerifications } from '@/app/dashboard/invoicing/verifications/use-indeterminate-verifications';
import {
  GENERAL_ERRAND_ACTIVE_STATUSES,
  INSPECTION_ERRAND_ACTIVE_STATUSES,
} from '@/app/dashboard/technical-management/constants';
import { IntegrationServiceDataMap } from '@/app/settings/organization/integrations/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { useEnvironment } from '@/hooks/use-environment';
import { useMenu } from '@/hooks/useMenu';
import useMonitoringCounts from '@/hooks/useMonitoringCounts';
import { usePerms } from '@/hooks/usePerms';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { ObjectEntries, ObjectKeys } from '@/lib/utils';
import { useSession } from '@/providers/session-provider';
import { useGetCount } from '@/requests/hooks';
import { useFetchAllInstances } from '@/requests/hooks/use-fetch-all-instances';
import type { DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Bars3Icon } from '@heroicons/react/16/solid';
import { useGetUnreadCount, useIsChatInitialized } from '@pigello/pigello-chat';
import {
  GeneralErrandConfig,
  InspectionErrandConfig,
  InvoiceConfig,
  type GeneralErrand,
  type InspectionErrand,
} from '@pigello/pigello-matrix';
import { uniqBy } from 'es-toolkit/compat';
import { menuData } from './data';
import { reorder } from '../utils';
import { SidebarBookmarks } from '../shared/bookmarks';
import { SubMenu } from '../shared/sub-menu';
import type { CheckedState, ISubMenu } from '../types';

export function DashboardNavigation() {
  const isChatInitialized = useIsChatInitialized();
  if (!isChatInitialized) {
    return null;
  }

  return <InnerDashboardNavigation />;
}

function InnerDashboardNavigation() {
  const { isEditMode, setIsEditMode, sidebarSettings, setSidebarSettings } =
    useMenu();
  const { addToast } = useToast();
  const {
    totalCount,
    totalCountLoading,
    performCount,
    approvalCount,
    retroactivityCount,
    totalPendingDistributionQuotas,
    pendingSigningCounts,
    performCountLoading,
    approvalCountLoading,
    retroactivityCountLoading,
    pendingDistributionQuotasLoading,
    pendingSigningCountsLoading,
  } = useMonitoringCounts();

  const { canView: canViewErrandDelegations } = usePerms(
    'erranddelegationoffer'
  );
  const { canView: canViewErrands } = usePerms('generalerrand');

  const { isTestEnvironment } = useEnvironment();

  const { canView: canViewInspections } = usePerms('inspectionerrand');

  const { data: autogiroIntegrationData } = useGetCount({
    modelName: 'integrationuseragent',
    filters: {
      application_id: {
        noop: IntegrationServiceDataMap.AUTOGIRO?.applicationId,
      },
      is_active: { noop: true },
    },
  });
  // SPECIAL CASE FOR DEV ENVIRONMENT ONLY
  const { data: autogiroDevIntegrationData } = useGetCount({
    modelName: 'integrationuseragent',
    filters: {
      application_id: {
        noop: IntegrationServiceDataMap.AUTOGIRO_DEV?.applicationId,
      },
      is_active: { noop: true },
    },
    enabled: isTestEnvironment,
  });

  const hasDevAutogiroIntegration =
    !!autogiroDevIntegrationData && autogiroDevIntegrationData > 0;
  const hasProdAutogiroIntegration =
    !!autogiroIntegrationData && autogiroIntegrationData > 0;

  const hasAnyAutogiroIntegration = isTestEnvironment
    ? hasDevAutogiroIntegration || hasProdAutogiroIntegration
    : hasProdAutogiroIntegration;
  const { canView, isPending } = usePerms('invoice');
  const {
    data: pendingAutogiroTransactionAttempts,
    isLoading: pendingAutogiroTransactionAttemptsLoading,
  } = useGetCount({
    modelName: InvoiceConfig.modelName,
    filters: {
      ...getPendingAutogiroTransactionAttemptsFilters(),
    },
    enabled: hasAnyAutogiroIntegration && canView && !isPending,
  });
  const {
    data: failedAutogiroTransactionAttempts,
    isLoading: failedAutogiroTransactionAttemptsLoading,
  } = useGetCount({
    modelName: InvoiceConfig.modelName,
    filters: {
      ...getFailedAutogiroTransactionAttemptsFilters(),
    },
    enabled: hasAnyAutogiroIntegration && canView && !isPending,
  });
  const {
    data: {
      session: { userId },
    },
  } = useSession();
  const {
    data: pendingDelegatedErrands,
    isLoading: isPendingDelegatedErrandsLoading,
  } = useFetchAllInstances<GeneralErrand>({
    modelName: GeneralErrandConfig.modelName,
    slim: true,
    filters: {
      users_unanswered_delegations: { noop: userId },
      status: {
        __in: GENERAL_ERRAND_ACTIVE_STATUSES.join(','),
      },
    },
    enabled: !!userId && canViewErrandDelegations && canViewErrands,
  });
  const {
    data: pendingDelegatedErrandsAsPlanner,
    isLoading: isPendingDelegatedErrandsAsPlannerLoading,
  } = useFetchAllInstances<GeneralErrand>({
    modelName: GeneralErrandConfig.modelName,
    slim: true,
    filters: {
      users_unanswered_delegations_planner: { noop: userId },
      status: {
        __in: GENERAL_ERRAND_ACTIVE_STATUSES.join(','),
      },
    },
    enabled: !!userId && canViewErrandDelegations && canViewErrands,
  });
  const {
    data: pendingDelegatedInspections,
    isLoading: isPendingDelegatedInspectionsLoading,
  } = useFetchAllInstances<InspectionErrand>({
    modelName: InspectionErrandConfig.modelName,
    slim: true,
    filters: {
      users_unanswered_delegations: { noop: userId },
      status: {
        __in: INSPECTION_ERRAND_ACTIVE_STATUSES.join(','),
      },
    },
    enabled: !!userId && canViewErrandDelegations && canViewInspections,
  });
  const {
    data: pendingDelegatedInspectionsAsPlanner,
    isLoading: isPendingDelegatedInspectionsAsPlannerLoading,
  } = useFetchAllInstances<InspectionErrand>({
    modelName: InspectionErrandConfig.modelName,
    slim: true,
    filters: {
      users_unanswered_delegations_planner: { noop: userId },
      status: {
        __in: INSPECTION_ERRAND_ACTIVE_STATUSES.join(','),
      },
    },
    enabled: !!userId && canViewErrandDelegations && canViewInspections,
  });

  const mergedPendingDelegatedInspectionsCount =
    uniqBy(
      [
        ...(pendingDelegatedInspections?.list ?? []),
        ...(pendingDelegatedInspectionsAsPlanner?.list ?? []),
      ],
      'id'
    ).length ?? 0;

  const mergedPendingDelegatedErrandsCount =
    uniqBy(
      [
        ...(pendingDelegatedErrands?.list ?? []),
        ...(pendingDelegatedErrandsAsPlanner?.list ?? []),
      ],
      'id'
    ).length ?? 0;

  const { data: failedDeliveriesCount, isLoading: failedDeliveriesLoading } =
    useFailedInvoices(true);

  const { groups } = useGetUnreadCount({ includeNotificationsOff: false });

  const {
    indeterminateVerificationsCount,
    isLoading: isLoadingIndeterminateVerifications,
  } = useIndeterminateVerifications();

  const chatUnreadCount = groups?.organizationUserGroups.length ?? 0;

  const inboxCount =
    totalCount +
    mergedPendingDelegatedInspectionsCount +
    mergedPendingDelegatedErrandsCount +
    chatUnreadCount;

  const notifications = {
    ['chat']: chatUnreadCount,
    ['monitor-inbox']: totalCountLoading ? totalCountLoading : inboxCount,
    ['monitored-performs']: performCountLoading
      ? performCountLoading
      : performCount,
    ['monitored-proposals']: approvalCountLoading
      ? approvalCountLoading
      : approvalCount,
    ['retroactivity']: retroactivityCountLoading
      ? retroactivityCountLoading
      : retroactivityCount,
    ['distribution-quotas']: pendingDistributionQuotasLoading
      ? pendingDistributionQuotasLoading
      : totalPendingDistributionQuotas,
    ['pending-errands']:
      isPendingDelegatedErrandsLoading ||
      isPendingDelegatedErrandsAsPlannerLoading
        ? isPendingDelegatedErrandsLoading ||
          isPendingDelegatedErrandsAsPlannerLoading
        : mergedPendingDelegatedErrandsCount,
    ['pending-inspections']:
      isPendingDelegatedInspectionsLoading ||
      isPendingDelegatedInspectionsAsPlannerLoading
        ? isPendingDelegatedInspectionsLoading ||
          isPendingDelegatedInspectionsAsPlannerLoading
        : mergedPendingDelegatedInspectionsCount,
    ['pending-signings']: pendingSigningCountsLoading
      ? pendingSigningCountsLoading
      : pendingSigningCounts,
    ['rent']:
      (failedAutogiroTransactionAttempts || 0) +
      (pendingAutogiroTransactionAttempts || 0) +
      (failedDeliveriesCount?.count || 0),
    ['autogiropending']: pendingAutogiroTransactionAttemptsLoading
      ? pendingAutogiroTransactionAttemptsLoading
      : pendingAutogiroTransactionAttempts,
    ['autogirofailed']: failedAutogiroTransactionAttemptsLoading
      ? failedAutogiroTransactionAttemptsLoading
      : failedAutogiroTransactionAttempts,
    ['faileddeliveries']: failedDeliveriesLoading
      ? failedDeliveriesLoading
      : (failedDeliveriesCount?.count ?? 0),
    ['verifications']: isLoadingIndeterminateVerifications
      ? isLoadingIndeterminateVerifications
      : (indeterminateVerificationsCount ?? 0),
  };

  const handleDragEnd = (result: DropResult, key: string) => {
    if (!result.destination) return;
    const curr =
      sidebarSettings.menuOrder?.[key] ??
      menuData[key as keyof typeof menuData].subMenus.map((item) => item.key);

    const newItems = reorder(
      curr,
      result.source.index,
      result.destination.index
    );
    setSidebarSettings((prev) => ({
      ...prev,
      menuOrder: {
        ...prev.menuOrder,
        [key]: newItems,
      },
    }));
  };

  const handleRootDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === 0) {
      return addToast({
        title: 'Meny måste vara högst upp',
        type: 'info',
      });
    }
    const curr =
      sidebarSettings.menuOrder?.['root'] ??
      ObjectKeys(menuData).map((key) => key);
    const newItems = reorder(
      curr,
      result.source.index,
      result.destination.index
    );
    setSidebarSettings((prev) => ({
      ...prev,
      menuOrder: {
        ...prev.menuOrder,
        root: newItems,
      },
    }));
  };

  const handleParentIsMenuVisible = (
    checked: CheckedState,
    subMenu: ISubMenu[],
    key: string
  ) => {
    const newState: Record<string, boolean> = {
      ...sidebarSettings.menuVisibility,
      [key]: !checked,
      ...subMenu.reduce((acc, { key }) => ({ ...acc, [key]: !checked }), {}),
      ...subMenu.reduce(
        (acc, { subMenus }) =>
          subMenus?.reduce(
            (acc, { key }) => ({ ...acc, [key]: !checked }),
            acc
          ) ?? acc,
        {}
      ),
    };
    setSidebarSettings((prev) => ({
      ...prev,
      menuVisibility: newState,
    }));
  };

  const filteredMenuData = Object.fromEntries(
    ObjectEntries(menuData).map(([key, value]) => {
      // Filter out autogiro related menus from the rent submenu
      const filteredSubMenus = value.subMenus?.map((submenu) => {
        if (submenu.key === 'rent') {
          return {
            ...submenu,
            subMenus: submenu.subMenus?.filter(
              (rentSubmenu) =>
                hasAnyAutogiroIntegration ||
                (rentSubmenu.key !== 'autogirofailed' &&
                  rentSubmenu.key !== 'autogiropending')
            ),
          };
        }
        return submenu;
      });

      return [
        key,
        {
          ...value,
          subMenus: filteredSubMenus,
        },
      ];
    })
  );

  return (
    <DragDropContext onDragEnd={handleRootDragEnd}>
      <Droppable isDropDisabled={!isEditMode} droppableId={'root'}>
        {(provided) => (
          <Accordion
            ref={provided.innerRef}
            {...provided.droppableProps}
            defaultValue={[
              'userMenu',
              'objects',
              'modules',
              'processes',
              'bookmarks',
            ]}
            onValueChange={(values) => {
              setSidebarSettings((prev) => ({
                ...prev,
                expandedItems: {
                  ...prev.expandedItems,
                  root: values,
                },
              }));
            }}
            value={sidebarSettings.expandedItems?.['root']}
            className={cn('w-full pr-4', !isEditMode && 'pl-4')}
            type='multiple'
          >
            {ObjectEntries(filteredMenuData)
              .filter(
                ([_, { key, isIndex }]) =>
                  isIndex ||
                  !(isEditMode ? {} : sidebarSettings.menuVisibility)?.[key]
              )
              .sort(
                (a, b) =>
                  sidebarSettings.menuOrder?.['root']?.indexOf(a[0]) -
                  sidebarSettings.menuOrder?.['root']?.indexOf(b[0])
              )
              .map(
                (
                  [groupType, { title, subMenus, isIndex, key: parentKey }],
                  idx
                ) => (
                  <Draggable
                    index={idx}
                    isDragDisabled={!isEditMode || isIndex}
                    draggableId={title + groupType + parentKey}
                    key={groupType}
                  >
                    {(provided) => (
                      <>
                        <AccordionItem
                          value={groupType}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className='w-full border-none'
                        >
                          {isIndex ? (
                            <div
                              className={cn(
                                'group/menu mt-1 flex items-center justify-between pl-6',
                                !isEditMode && 'pl-2'
                              )}
                            >
                              <h4
                                className={cn(
                                  'flex items-center gap-2 py-1 text-xs font-normal text-foreground/90',
                                  sidebarSettings.menuVisibility?.[parentKey] &&
                                    !isIndex &&
                                    'opacity-50'
                                )}
                              >
                                {title}
                              </h4>
                              <div className='flex items-center gap-2'>
                                {isEditMode && (
                                  <button
                                    className={cn(
                                      'hidden text-xs text-muted-foreground transition-colors group-hover/menu:block hover:text-foreground',
                                      isEditMode && 'block'
                                    )}
                                    onClick={() =>
                                      setSidebarSettings({
                                        menuOrder: {},
                                        menuVisibility: {},
                                        expandedItems: {},
                                      })
                                    }
                                  >
                                    Återställ
                                  </button>
                                )}
                                <button
                                  className={cn(
                                    'hidden text-xs text-muted-foreground transition-colors group-hover/menu:block hover:text-foreground',
                                    isEditMode && 'block'
                                  )}
                                  onClick={() => setIsEditMode((prev) => !prev)}
                                >
                                  {isEditMode ? 'Klar' : 'Redigera'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className='group/root-item flex w-full items-start'>
                              {isEditMode && (
                                <button
                                  {...provided.dragHandleProps}
                                  className={cn(
                                    'invisible mt-3 ml-1 cursor-grab transition-opacity group-hover/root-item:visible'
                                  )}
                                >
                                  <Bars3Icon className='size-4' />
                                </button>
                              )}
                              <div
                                className={cn(
                                  'mt-2 flex w-full items-center rounded py-2 pr-2 text-left text-sm text-secondary-foreground transition-colors hover:bg-accent'
                                )}
                              >
                                <AccordionTrigger
                                  icon={!isEditMode}
                                  className={cn(
                                    'p-0 text-left text-foreground/90 hover:no-underline [&>svg]:opacity-0 [&>svg]:transition-all hover:[&>svg]:opacity-100',
                                    sidebarSettings.menuVisibility?.[
                                      parentKey
                                    ] &&
                                      !isIndex &&
                                      'opacity-50'
                                  )}
                                >
                                  <span className='w-full pl-2 text-xs font-normal'>
                                    {title}
                                  </span>
                                </AccordionTrigger>
                                {isEditMode && !isIndex && (
                                  <Checkbox
                                    checked={
                                      !sidebarSettings.menuVisibility?.[
                                        parentKey
                                      ] && !isIndex
                                    }
                                    onCheckedChange={(checked) =>
                                      handleParentIsMenuVisible(
                                        checked,
                                        subMenus,
                                        parentKey
                                      )
                                    }
                                    className='size-4'
                                  />
                                )}
                              </div>
                            </div>
                          )}
                          <AccordionContent>
                            <DragDropContext
                              onDragEnd={(e) => handleDragEnd(e, parentKey)}
                            >
                              <Droppable
                                type={parentKey}
                                droppableId={groupType}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                  >
                                    {subMenus
                                      .filter(
                                        ({ key }) =>
                                          !(
                                            isEditMode
                                              ? {}
                                              : sidebarSettings.menuVisibility
                                          )?.[key]
                                      )
                                      .sort(
                                        (a, b) =>
                                          sidebarSettings.menuOrder?.[
                                            parentKey
                                          ]?.indexOf(a.key) -
                                          sidebarSettings.menuOrder?.[
                                            parentKey
                                          ]?.indexOf(b.key)
                                      )
                                      .map((menu, idx) => (
                                        <SubMenu
                                          key={menu.key}
                                          index={idx}
                                          parentKey={parentKey}
                                          menu={menu}
                                          subMenus={subMenus}
                                          notifications={notifications}
                                        />
                                      ))}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </DragDropContext>
                            <div className='mx-2 mt-3 border-t border-border pb-1' />
                          </AccordionContent>
                        </AccordionItem>
                      </>
                    )}
                  </Draggable>
                )
              )}
            {provided.placeholder}
          </Accordion>
        )}
      </Droppable>
      <SidebarBookmarks />
    </DragDropContext>
  );
}
